import { useEffect, useState } from 'react';
import { useAuthUser } from 'app/hooks/useAuthUser';
import { imageConfig } from './imageConfig';
import { generateOptimizedImageUrl, parseImageUrl } from './imageUrlParser';

/**
 * Custom hook para manejar la lógica de imágenes responsivas optimizadas
 * @param {string} src - URL de la imagen
 * @param {number} sizeMax - Tamaño máximo disponible para simulación (opcional)
 * @param {string} sizes - String de sizes personalizado (opcional)
 * @returns {object} - Objeto con toda la lógica y valores necesarios para renderizar
 */
function useImage({ src, sizeMax, sizes }) {
  const { authToken } = useAuthUser();
  const isLocalUrl = src ? /^(\/|\.\/|\.\.\/)?images\/|^\/assets\/|^blob:|^data:image\//.test(src) : false;
  const isExternalUrl = src ? /img\.youtube\.com|ytimg\.com|vimeo\.com|\.gif(\?|$)/i.test(src) : false;

  const [maxSizeIndex, setMaxSizeIndex] = useState(0);

  useEffect(() => {
    setMaxSizeIndex(0);
  }, [src]);

  let availableSizes = imageConfig['default-images'];
  if (sizeMax > 0) {
    availableSizes = imageConfig['extra-images'].filter((size) => size <= sizeMax);
  }

  /**
   * Genera el srcset con las variantes de imagen optimizadas disponibles
   * @param {string} originalUrl - URL original de la imagen
   * @param {number} maxSizeIndex - Índice máximo del tamaño a incluir (0 = todos, 1 = excluir el más grande, etc.)
   * @param {array} availableSizes - Array de tamaños disponibles (ya filtrados por maxAvailableSize)
   * @returns {string} - String srcset formateado
   */
  function generateSrcset(originalUrl, maxSizeIndex = 0, availableSizes = []) {
    if (!originalUrl || isLocalUrl || isExternalUrl) return '';

    try {
      const parsed = parseImageUrl(originalUrl);
      if (!parsed) return '';

      const sizesToInclude =
        maxSizeIndex > 0 && maxSizeIndex < availableSizes.length ? availableSizes.slice(0, availableSizes.length - maxSizeIndex) : availableSizes;

      const srcsetEntries = sizesToInclude.map((imageSize) => {
        const imageUrl = generateOptimizedImageUrl(originalUrl, imageSize, imageConfig['image-output-format'], authToken);
        return `${imageUrl} ${imageSize}w`;
      });

      return srcsetEntries.join(', ');
    } catch (error) {
      console.error('Image: Error generando srcset:', error);
      return '';
    }
  }

  /**
   * Genera el string de sizes para imágenes responsivas
   * @returns {string} - String sizes formateado para media queries
   */
  function generateSizes() {
    if (sizes) return sizes;

    return `(max-width: 480px) 50vw, (max-width: 1023px) 50vw, 800px`;
  }

  /**
   * Genera la URL de fallback optimizada
   * @param {string} originalUrl - URL original de la imagen
   * @param {array} availableSizes - Array de tamaños disponibles
   * @returns {string} - URL de fallback optimizada
   */
  function getFallbackUrl(originalUrl, availableSizes = []) {
    if (!originalUrl) return '';

    if (originalUrl.includes('.avif') || originalUrl.includes('.webp')) {
      return originalUrl;
    }

    if (isLocalUrl || isExternalUrl) {
      return originalUrl;
    }

    try {
      const parsed = parseImageUrl(originalUrl);
      if (!parsed) return originalUrl;

      const maxSize = availableSizes.length > 0 ? Math.max(...availableSizes) : Math.max(...imageConfig['extra-images']);
      return generateOptimizedImageUrl(originalUrl, maxSize, imageConfig['image-output-format'], authToken);
    } catch (error) {
      console.error('Image: Error generando fallback URL:', error);
    }

    return originalUrl;
  }

  function handleImageError() {
    if (!src || isLocalUrl || isExternalUrl) return;

    if (maxSizeIndex < availableSizes.length) {
      setMaxSizeIndex(maxSizeIndex + 1);
    }
  }

  const srcset = generateSrcset(src, maxSizeIndex, availableSizes);
  const sizesString = generateSizes();
  const fallbackUrl = getFallbackUrl(src, availableSizes);

  const hasExhaustedAllSizes = maxSizeIndex >= availableSizes.length;
  const imageSrc = src;
  const imageSrcSet = hasExhaustedAllSizes ? '' : srcset;

  return {
    imageSrc,
    imageSrcSet,
    sizesString,
    fallbackUrl,
    srcset,
    handleImageError,
  };
}

export default useImage;
